import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import {
  WalteroCard,
  WalteroTypography,
  WalteroButton,
  WalteroTabPanel
} from './waltero';
import { hasOrganizationAccess } from '../utils/auth';
import { API_BASE_URL, ADMIN_ORGANIZATION_ID } from '../config/api';
import axios from 'axios';

// Import child components
import ApiUsageDashboard from './apiUsage/ApiUsageDashboard';
import ApiUsageUsersList from './apiUsage/ApiUsageUsersList';
import ApiUsageEndpoints from './apiUsage/ApiUsageEndpoints';
import ApiUsageDetailedLogs from './apiUsage/ApiUsageDetailedLogs';
import ApiUsageTrends from './apiUsage/ApiUsageTrends';

/**
 * API Usage Tab Component
 * Admin-only tab for monitoring API usage across the platform
 */
const ApiUsageTab = () => {
  // Check admin access
  const isAdmin = hasOrganizationAccess(ADMIN_ORGANIZATION_ID);

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [endpointsData, setEndpointsData] = useState(null);
  const [logsData, setLogsData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📊 Loading API usage dashboard for period:', period);
      const response = await axios.get(`${API_BASE_URL}/admin/api-usage/dashboard?period=${period}`);
      setDashboardData(response.data.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load API usage dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period]);

  // Load users usage data
  const loadUsersData = useCallback(async (page = 1, search = '') => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📊 Loading users API usage data');
      const params = new URLSearchParams({
        period,
        page,
        limit: 50,
        sortBy: 'total_calls',
        sortOrder: 'desc'
      });

      if (search) {
        params.append('search', search);
      }

      const response = await axios.get(`${API_BASE_URL}/admin/api-usage/users?${params}`);
      setUsersData(response.data.data);
    } catch (err) {
      console.error('Failed to load users data:', err);
      setError('Failed to load users usage data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period]);

  // Load endpoints data
  const loadEndpointsData = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📊 Loading endpoints usage data');
      const response = await axios.get(`${API_BASE_URL}/admin/api-usage/endpoints?period=${period}`);
      console.log('📊 Endpoints API response:', response.data);
      setEndpointsData(response.data.data);
    } catch (err) {
      console.error('Failed to load endpoints data:', err);
      setError('Failed to load endpoints data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, period]);

  // Load detailed logs
  const loadLogsData = useCallback(async (page = 1, filters = {}) => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📊 Loading detailed API logs');
      const params = new URLSearchParams({
        startDate: filters.startDate || dateRange.startDate,
        endDate: filters.endDate || dateRange.endDate,
        page,
        limit: 100
      });

      if (filters.userId) params.append('userId', filters.userId);
      if (filters.endpoint) params.append('endpoint', filters.endpoint);
      if (filters.statusCode) params.append('statusCode', filters.statusCode);

      const response = await axios.get(`${API_BASE_URL}/admin/api-usage/reports?${params}`);
      setLogsData(response.data.data);
    } catch (err) {
      console.error('Failed to load logs data:', err);
      setError('Failed to load API logs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, dateRange]);

  // Load trends data
  const loadTrendsData = useCallback(async (granularity = 'day') => {
    if (!isAdmin) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📊 Loading API usage trends');
      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        granularity
      });

      const response = await axios.get(`${API_BASE_URL}/admin/api-usage/trends?${params}`);
      setTrendsData(response.data.data);
    } catch (err) {
      console.error('Failed to load trends data:', err);
      setError('Failed to load trends data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, dateRange]);

  // Initial load based on active tab
  useEffect(() => {
    if (!isAdmin) return;

    switch (activeTab) {
      case 0:
        loadDashboardData();
        break;
      case 1:
        loadUsersData(1, searchTerm);
        break;
      case 2:
        loadEndpointsData();
        break;
      case 3:
        loadLogsData();
        break;
      case 4:
        loadTrendsData();
        break;
      default:
        break;
    }
  }, [activeTab, isAdmin, loadDashboardData, loadUsersData, loadEndpointsData, loadLogsData, loadTrendsData]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
  };

  // Handle period change
  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  // Handle export
  const handleExport = async (type) => {
    let url = null;
    try {
      const params = new URLSearchParams({
        format: 'csv',
        period,
        type
      });

      const response = await axios.get(`${API_BASE_URL}/admin/api-usage/export?${params}`, {
        responseType: 'blob'
      });

      // Create download link
      url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `api-usage-${type}-${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to export data:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      // Clean up the blob URL to prevent memory leaks
      if (url) {
        window.URL.revokeObjectURL(url);
      }
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    switch (activeTab) {
      case 0:
        loadDashboardData();
        break;
      case 1:
        loadUsersData(1, searchTerm);
        break;
      case 2:
        loadEndpointsData();
        break;
      case 3:
        loadLogsData();
        break;
      case 4:
        loadTrendsData();
        break;
      default:
        break;
    }
  };

  // Check admin access
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <WalteroTypography variant="h6">Access Denied</WalteroTypography>
          <WalteroTypography variant="body2">
            You do not have permission to view API usage data. This feature is restricted to administrators.
          </WalteroTypography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: '#fdf3eb', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <WalteroTypography variant="h4" color="primary">
              API Usage Monitor
            </WalteroTypography>
            <WalteroTypography variant="body2" color="textSecondary">
              Track and analyze API usage across the platform
            </WalteroTypography>
          </Grid>
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 150, mr: 2 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={period}
                onChange={handlePeriodChange}
                label="Period"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() - i);
                  const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                  const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  return (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton
                onClick={() => handleExport(activeTab === 1 ? 'users' : activeTab === 2 ? 'endpoints' : 'summary')}
                color="primary"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Dashboard" />
          <Tab label="Users" />
          <Tab label="Endpoints" />
          <Tab label="Detailed Logs" />
          <Tab label="Trends" />
        </Tabs>
      </Box>

      {/* Error Alert */}
      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tab Content */}
      {!loading && (
        <>
          <WalteroTabPanel value={activeTab} index={0}>
            <ApiUsageDashboard
              data={dashboardData}
              period={period}
              onUserClick={(userId) => {
                setSelectedUser(userId);
                setActiveTab(3);
              }}
              onEndpointClick={(endpoint) => {
                setSelectedEndpoint(endpoint);
                setActiveTab(3);
              }}
            />
          </WalteroTabPanel>

          <WalteroTabPanel value={activeTab} index={1}>
            <ApiUsageUsersList
              data={usersData}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onUserSelect={(userId) => {
                setSelectedUser(userId);
                setActiveTab(3);
              }}
              onPageChange={(page) => loadUsersData(page, searchTerm)}
            />
          </WalteroTabPanel>

          <WalteroTabPanel value={activeTab} index={2}>
            <ApiUsageEndpoints
              data={endpointsData}
              onEndpointClick={(endpoint) => {
                setSelectedEndpoint(endpoint);
                setActiveTab(3);
              }}
            />
          </WalteroTabPanel>

          <WalteroTabPanel value={activeTab} index={3}>
            <ApiUsageDetailedLogs
              data={logsData}
              filters={{
                userId: selectedUser,
                endpoint: selectedEndpoint,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate
              }}
              onFiltersChange={(newFilters) => {
                setSelectedUser(newFilters.userId || '');
                setSelectedEndpoint(newFilters.endpoint || '');
                setDateRange({
                  startDate: newFilters.startDate || dateRange.startDate,
                  endDate: newFilters.endDate || dateRange.endDate
                });
                loadLogsData(1, newFilters);
              }}
              onPageChange={(page) => loadLogsData(page)}
            />
          </WalteroTabPanel>

          <WalteroTabPanel value={activeTab} index={4}>
            <ApiUsageTrends
              data={trendsData}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              onGranularityChange={(granularity) => loadTrendsData(granularity)}
            />
          </WalteroTabPanel>
        </>
      )}
    </Box>
  );
};

export default ApiUsageTab;